import React, { useState } from 'react';
import { useToast } from "@/components/UI/use-toast";
import { misApi } from '@/services/misApi';
import { Button } from "@/components/UI/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/UI/card";
import { Upload, FileUp, Loader2, RefreshCw, Database } from 'lucide-react';

const TransactionUpload = ({ onUploadComplete }) => {
  const { toast } = useToast();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type.includes('excel') || 
          selectedFile.type.includes('spreadsheetml') ||
          selectedFile.name.endsWith('.xlsx') || 
          selectedFile.name.endsWith('.xls')) {
        setFile(selectedFile);
      } else {
        toast({
          variant: "destructive",
          title: "Invalid file type",
          description: "Please upload an Excel file (.xlsx or .xls)",
          duration: 3000,
        });
      }
    }
  };
  
  const handleUpload = async () => {
    if (!file) {
      toast({
        variant: "destructive",
        title: "No file selected",
        description: "Please select an Excel file to upload",
        duration: 3000,
      });
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const response = await misApi.uploadTransactions(
        file, 
        (progress) => setUploadProgress(progress)
      );
      
      toast({
        title: "Upload Successful",
        description: `File processed with ${response.data.data.rowCount} rows`,
        duration: 3000,
      });
      
      setFile(null);
      
      if (onUploadComplete) {
        onUploadComplete(response.data.data.processedMonths);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast({
        variant: "destructive",
        title: "Upload Failed",
        description: error.response?.data?.message || "Failed to process the file",
        duration: 5000,
      });
    } finally {
      setUploading(false);
    }
  };
  
  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg font-medium flex items-center">
          <Database className="h-5 w-5 mr-2 text-[#C5A572]" />
          Upload Transaction Data
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-4">
          <div className="flex flex-col space-y-2">
            <Button 
              variant="outline" 
              onClick={() => document.getElementById('file-upload').click()}
              disabled={uploading}
              className="w-full py-8 border-dashed border-2 hover:border-[#C5A572]"
            >
              <Upload className="h-6 w-6 mr-2" />
              <span className="font-medium">Select Excel File</span>
            </Button>
            
            <input
              id="file-upload"
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>
          
          {file && (
            <div className="bg-blue-50 p-3 rounded-md border border-blue-100">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-blue-700">
                  {file.name}
                </span>
                <span className="text-xs text-blue-500">
                  {(file.size / 1024).toFixed(2)} KB
                </span>
              </div>
            </div>
          )}
          
          {uploading && (
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-[#C5A572] h-2.5 rounded-full" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
              <p className="text-xs text-gray-500 mt-1 text-right">{uploadProgress}% uploaded</p>
            </div>
          )}
          
          <Button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="w-full bg-[#C5A572] hover:bg-[#a68445]"
          >
            {uploading ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Upload className="h-4 w-4 mr-2" />
            )}
            {uploading ? "Processing..." : "Upload & Process"}
          </Button>
        </div>
        
        <div className="border-t pt-4 mt-4">
          <div className="text-xs text-gray-500 space-y-1">
            <p className="font-medium">Note:</p>
            <ul className="list-disc pl-4 space-y-1">
              <li>Upload Excel file with transaction data</li>
              <li>The system will automatically calculate profit and loss data for all months in the file</li>
              <li>For revenue calculation, accounts starting with '4' will be used</li>
              <li>Your file should contain columns: Date, Account, D/C, AED and other transaction details</li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TransactionUpload;